import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus,
  User,
  Mail,
  Phone,
  BadgeCheck,
  ChevronDown,
  Search,
  Check,
  X,
  Plus,
  ShieldCheck,
  Lock,
  ScanFace,
  Landmark,
  ShieldAlert,
  Newspaper,
  ListChecks,
  Info,
  CalendarClock,
  Loader2,
} from 'lucide-react';
import { useToast } from '../../Utils/toastContext';
import countriesData from '../../Data/CountryCodes.json';
/* Adjust the path if CustomSelect lives elsewhere in your project */
import CustomSelect from '../../Components/customs/dropdown';

/* ========================================================================== */
/*  API INTEGRATION                                                           */
/*  · GET /api/v1/agency/customers/account-types                              */
/*      → Application type dropdown options + account_type_id mapping (GAP-08a)*/
/*  · GET /api/v1/agency/verification-package-defaults/catalog                */
/*      → Verification checklist list, Identity lock flags and the default    */
/*        pre-select hints. Codes from this catalog ARE the item_codes sent   */
/*        with the invite.                                                    */
/* ========================================================================== */

/* ------------------------- Verifications -------------------------- */
/* Static copy — used ONLY as a placeholder while the catalog loads and
   as a last resort if it fails. The rendered list + selection are
   driven by the catalog API (same codes, same UI). */
const VERIFICATIONS = [
  {
    key: 'identity',
    label: 'Identity Verification',
    desc: 'KYC documents & biometric identity match',
    icon: ScanFace,
    required: true, // mandatory — can never be removed
  },
  {
    key: 'pep',
    label: 'PEP Screening',
    desc: 'Politically exposed persons database check',
    icon: Landmark,
  },
  {
    key: 'sanctions',
    label: 'Sanctions Screening',
    desc: 'UN, OFAC & local sanctions lists',
    icon: ShieldAlert,
  },
  {
    key: 'adverse_media',
    label: 'Adverse Media',
    desc: 'Negative news & media mentions',
    icon: Newspaper,
  },
  {
    key: 'watchlist',
    label: 'Watchlist',
    desc: 'AML & counter-terror watchlists',
    icon: ListChecks,
  },
];

/* Safety net used ONLY if the catalog API fails — identity is the one
   package the backend always requires, so we never invent extras. */
const FALLBACK_VERIFICATIONS = ['identity'];

const MANDATORY_VERIFICATIONS = VERIFICATIONS.filter((v) => v.required).map(
  (v) => v.key,
);

/* Codes the backend applies automatically and REJECTS if echoed back:
   "identity is always implied and must not appear in item_codes".
   They stay visible (and locked) in the checklist, but are stripped
   before the invite is posted. */
const IMPLIED_VERIFICATIONS = ['identity'];

/* checklist selection → invite payload item_codes (implied codes removed) */
const toItemCodes = (selected) =>
  (selected || []).filter((k) => !IMPLIED_VERIFICATIONS.includes(k));

/* First arg that isn't null/undefined — for tolerant response mapping. */
const firstDefined = (...vals) =>
  vals.find((v) => v !== undefined && v !== null);

/* Code → icon, so catalog-driven rows render identically to the static UI.
   Unknown packages get a neutral check shield. "media" is the catalog's
   code for Adverse media (the static list called it "adverse_media"). */
const VERIFICATION_ICONS = {
  identity: ScanFace,
  pep: Landmark,
  sanctions: ShieldAlert,
  adverse_media: Newspaper,
  media: Newspaper,
  watchlist: ListChecks,
};

/* Static copy by key — cosmetic fallback ONLY (label/desc the catalog rows
   don't carry). Never drives codes or selection. */
const STATIC_META_BY_KEY = Object.fromEntries(
  VERIFICATIONS.map((v) => [v.key, { label: v.label, desc: v.desc }]),
);
STATIC_META_BY_KEY.media = STATIC_META_BY_KEY.adverse_media; // catalog code

/* ------------------------------------------------------------------ */
/*  One raw package from the catalog → one checklist item.             */
/*  Field names are read tolerantly (code|item_code, name|label,       */
/*  required|locked|identity_locked, is_default|pre_selected|…).       */
/* ------------------------------------------------------------------ */
const normalizeCatalogItem = (raw = {}) => {
  const key = String(firstDefined(raw.code, raw.item_code, ''))
    .toLowerCase()
    .trim();
  if (!key) return null;
  return {
    key, // ← this IS the item_code echoed back on the invite (minus implied)
    label: String(firstDefined(raw.name, raw.label, key)),
    desc: String(
      firstDefined(
        raw.description,
        raw.desc,
        STATIC_META_BY_KEY[key]?.desc,
        '',
      ),
    ),
    icon: VERIFICATION_ICONS[key] || ShieldCheck,
    // Identity lock flags → locked, required, un-removable in the UI
    required: Boolean(
      firstDefined(
        raw.required,
        raw.identity_locked,
        raw.locked,
        raw.is_required,
        key === 'identity',
      ),
    ),
    // default pre-select hint from the catalog
    isDefault: Boolean(
      firstDefined(
        raw.is_default,
        raw.pre_selected,
        raw.default_selected,
        raw.selected_by_default,
        raw.default,
      ),
    ),
  };
};

/* Pull the package array out of the catalog response envelope. */
const pickCatalogItems = (data = {}) =>
  (Array.isArray(
    firstDefined(
      data.packages,
      data.catalog,
      data.items,
      data.verification_packages,
      [],
    ),
  )
    ? firstDefined(
        data.packages,
        data.catalog,
        data.items,
        data.verification_packages,
        [],
      )
    : []
  )
    .map(normalizeCatalogItem)
    .filter(Boolean);

/* Identity is implied by the backend and always shown locked — if the
   catalog ever omits it, synthesize the row so the UI stays honest. */
const withIdentity = (items) =>
  items.some((i) => i.key === 'identity')
    ? items
    : [
        {
          key: 'identity',
          label: 'Identity Verification',
          desc: 'KYC documents & biometric identity match',
          icon: ScanFace,
          required: true,
          isDefault: true,
        },
        ...items,
      ];

/* ---------------------- Certificate validity ----------------------- */
/* Payload: form.certValidity = '30' | '60' | '90' | 'none'            */
const CERT_VALIDITY_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: 'none', label: 'No expiry' },
];
const DEFAULT_CERT_VALIDITY = '90';

/* Part of the documented invite payload → always sent:
   certificate_validity_days: 30 | 60 | 90 | null (null = no expiry) */
const INCLUDE_CERTIFICATE_VALIDITY = true;

/* ---------------------- Account type mapping ----------------------- */
/* Static id map kept ONLY as a fallback — the primary mapping comes
   from GET /account-types (label → value sent as account_type_id). */
const ACCOUNT_TYPE_IDS = {
  Indiviudal: 1, // (sic) — the spelling previously used in the static list
  Individual: 1,
  'Joint Account': 2,
  'Sole Trader': 3,
};
const DEFAULT_ACCOUNT_TYPE_ID = 1;

/* Static enum mirrors — used ONLY if the account-types API ever fails,
   so the dropdown still opens with sensible values. */
const FALLBACK_ACCOUNT_TYPES = [
  { value: 1, name: 'individual', label: 'Individual' },
  { value: 2, name: 'joint_account', label: 'Joint Account' },
  { value: 3, name: 'sole_trader', label: 'Sole Trader' },
];

/* account_types rows → { value, name, label } for the dropdown + id map */
const normalizeAccountTypes = (raw) =>
  (Array.isArray(raw) ? raw : [])
    .map((t = {}) => ({
      value: Number(firstDefined(t.value, t.id, t.account_type_id)),
      name: String(t.name ?? '').trim(),
      label: String(firstDefined(t.label, t.name, '') ?? '').trim(),
    }))
    .filter((t) => t.label && Number.isFinite(t.value));

/* The invite API needs account_type_id but the UI stores the label.
   Look the label up in the fetched enum first, then the static map. */
const getAccountTypeId = (label, accountTypes) => {
  const hit = (accountTypes || []).find(
    (t) => t.label === label || t.name === label,
  );
  if (hit && Number.isFinite(hit.value)) return hit.value;
  return ACCOUNT_TYPE_IDS[label] ?? DEFAULT_ACCOUNT_TYPE_ID;
};

/* ------------------------------------------------------------------ */
/*  Defaults response → checklist selection                            */
/*  { item_codes: null, resolved_packages: ['identity','pep'], ... }   */
/*  item_codes (agency override) wins when it has values; identity is  */
/*  always merged in so the UI can show it as locked; anything we      */
/*  don't render is dropped. This drives the CHECKLIST only — it is    */
/*  NOT the invite payload (use toItemCodes for that).                 */
/* ------------------------------------------------------------------ */
const resolveSelectedVerifications = (data) => {
  const override = Array.isArray(data?.item_codes) ? data.item_codes : [];
  const codes = override.length
    ? override
    : firstDefined(
        data?.resolved_default_packages, // catalog field name
        data?.resolved_packages,
        data?.default_extras, // catalog field name (identity merged below)
        [],
      );

  const picked = new Set(
    (Array.isArray(codes) ? codes : []).map((c) => String(c).toLowerCase()),
  );
  MANDATORY_VERIFICATIONS.forEach((k) => picked.add(k));

  // keep the checklist's own order so the payload is stable
  return VERIFICATIONS.filter((v) => picked.has(v.key)).map((v) => v.key);
};

/* ------------------------------------------------------------------ */
/*  Catalog → the "enabled by default" selection.                      */
/*  A top-level codes array (already resolved server-side) wins when   */
/*  present; otherwise each item's own is_default hint. Locked items   */
/*  (identity) are always included. Catalog order is kept so the       */
/*  payload is stable. Drives the CHECKLIST only — item_codes still go */
/*  through toItemCodes().                                             */
/* ------------------------------------------------------------------ */
const resolveSelectedFromCatalog = (items, hintedCodes) => {
  const list = withIdentity(items);
  const hints = new Set(
    (Array.isArray(hintedCodes) ? hintedCodes : []).map((c) =>
      String(c).toLowerCase().trim(),
    ),
  );
  const useHints = hints.size > 0;
  return list
    .filter((i) => i.required || (useHints ? hints.has(i.key) : i.isDefault))
    .map((i) => i.key);
};

/* ------------------------------------------------------------------ */
/*  Form state factory — EVERY open starts from this exact object      */
/* ------------------------------------------------------------------ */
const buildInitialForm = () => ({
  firstName: '',
  lastName: '',
  email: '',
  appType: '', // ← filled from the account-types API once it resolves
  cc: '+61',
  mobile: '',
  verifications: [...FALLBACK_VERIFICATIONS],
  certValidity: DEFAULT_CERT_VALIDITY,
});

const HIDDEN_DD_STYLE = {
  position: 'fixed',
  visibility: 'hidden',
  left: 0,
  top: 0,
  zIndex: 60,
};

/* ------------------------------------------------------------------ */
/*  VerificationChecklist — compact selectable list in a soft panel    */
/*  value: string[] of selected keys · onChange(next: string[])        */
/*  loading: defaults are still being fetched (toggles disabled)       */
/*  items: catalog-driven rows (falls back to the static list)         */
/* ------------------------------------------------------------------ */
function VerificationChecklist({ value, onChange, loading = false, items }) {
  const list = Array.isArray(items) && items.length ? items : VERIFICATIONS;
  const selected = value || [];
  const count = selected.length;

  const toggle = (v) => {
    if (v.required || loading) return; // identity verification is mandatory
    const next = selected.includes(v.key)
      ? selected.filter((k) => k !== v.key)
      : [...selected, v.key];
    onChange(next);
  };

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-b from-slate-50/90 to-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
      {/* Panel header */}
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-md bg-brand/10 flex items-center justify-center text-brand">
            <ShieldCheck size={13} strokeWidth={2.2} />
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Verifications
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-2.5 py-[4px] text-[10.5px] font-bold text-brand">
          {loading ? (
            <Loader2 size={11} className="animate-spin" />
          ) : (
            `${count} of ${list.length}`
          )}
        </span>
      </div>

      {/* Rows */}
      <div className="space-y-1.5">
        {list.map((v) => {
          const Icon = v.icon;
          const on = v.required || selected.includes(v.key);
          return (
            <button
              key={v.key}
              type="button"
              onClick={() => toggle(v)}
              disabled={v.required || loading}
              aria-pressed={on}
              className={`group relative w-full flex items-center gap-3 rounded-xl border p-2.5 text-left transition-all duration-200 ${
                v.required
                  ? 'cursor-not-allowed border-brand/40 bg-gradient-to-r from-brand/[0.06] to-transparent'
                  : on
                    ? 'border-brand/30 bg-gradient-to-r from-brand/[0.05] to-transparent shadow-[0_4px_14px_-10px_rgba(55,90,231,0.45)]'
                    : 'border-slate-200 bg-white hover:-translate-y-px hover:border-slate-300 hover:bg-slate-50/60'
              }`}
            >
              {/* Icon chip */}
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-all duration-200 ${
                  on
                    ? 'bg-brand text-white shadow-md shadow-brand/30'
                    : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200/70 group-hover:text-slate-500'
                }`}
              >
                <Icon size={16} strokeWidth={1.9} />
              </span>

              {/* Copy */}
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span
                    className={`truncate text-[12.5px] font-bold ${
                      on ? 'text-slate-900' : 'text-slate-600'
                    }`}
                  >
                    {v.label}
                  </span>
                  {v.required && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-brand/25 bg-brand/10 px-1.5 py-[1.5px] text-[9px] font-bold uppercase tracking-wide text-brand">
                      <Lock size={8.5} strokeWidth={2.6} />
                      Required
                    </span>
                  )}
                </span>
                <span className="mt-px block truncate text-[10.5px] font-medium text-slate-400">
                  {v.desc}
                </span>
              </span>

              {/* Toggle */}
              <span
                className={`relative flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ${
                  on ? 'bg-brand' : 'bg-slate-200'
                }`}
                aria-hidden="true"
              >
                <span
                  className={`absolute h-5 w-5 rounded-full bg-white shadow-sm transition-all duration-200 ${
                    on
                      ? 'left-[22px] flex items-center justify-center text-brand'
                      : 'left-0.5'
                  }`}
                >
                  {v.required && <Lock size={10} strokeWidth={2.8} />}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Mandatory note */}
      <p className="mt-2.5 flex items-start gap-1.5 text-[10.5px] font-medium leading-relaxed text-slate-400">
        <Info size={12} className="mt-px shrink-0 text-brand/70" />
        Identity verification is mandatory and can't be removed. Defaults can be
        switched off; extras can be added.
      </p>
    </div>
  );
}

/* ================================================================== */
/*  InviteCustomerModal (compact 2-pane)                               */
/* ================================================================== */
function InviteCustomerModal({ open, onClose, onSave }) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [form, setForm] = useState(buildInitialForm);
  const [errors, setErrors] = useState({});
  const [appOpen, setAppOpen] = useState(false);
  const [appQuery, setAppQuery] = useState('');
  const [packageDefaultsLoading, setPackageDefaultsLoading] = useState(false);
  const [packageDefaults, setPackageDefaults] = useState(null); // raw catalog data
  const [catalogItems, setCatalogItems] = useState([]); // normalized catalog rows
  const [defaultSelection, setDefaultSelection] = useState([
    ...FALLBACK_VERIFICATIONS,
  ]); // catalog-resolved defaults (re-applied after each invite)
  const [accountTypes, setAccountTypes] = useState([]); // GAP-08a enum
  const [submitting, setSubmitting] = useState(false);
  const triggerRef = useRef(null);
  const ddRef = useRef(null);
  const [countries, setCountries] = useState([]);

  const [ddStyle, setDdStyle] = useState(HIDDEN_DD_STYLE);
  useEffect(() => {
    setCountries(countriesData);
  }, []);
  /* ------------------------------------------------------------------ */
  /*  Toast — always a 2-arg call with a lowercase, never-undefined      */
  /*  type, wrapped so a toastProvider problem can't fail an invite.     */
  /*  showToast is read from a ref so notify() keeps a stable identity   */
  /*  (an unstable callback here would re-run the defaults effect).      */
  /* ------------------------------------------------------------------ */
  const showToastRef = useRef(showToast);
  useEffect(() => {
    showToastRef.current = showToast;
  });

  const notify = useCallback((message, type = 'info') => {
    const text = String(message ?? '').trim();
    if (!text) return;
    const kind = String(type || 'info').toLowerCase(); // toastContext does type.toLowerCase()
    try {
      showToastRef.current?.(text, kind);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[InviteCustomerModal] showToast threw:', err, {
        message: text,
        type: kind,
      });
    }
  }, []);

  /* ------------------------------------------------------------------ */
  /*  GET helper — same shape as your shared GetAPIcalls                 */
  /*  (delete this if you already import the shared one)                 */
  /* ------------------------------------------------------------------ */
  const GetAPIcalls = useCallback(
    async (url, isBlob = false) => {
      const token = localStorage.getItem('jwtToken');
      const apiUrl = `${BASE_URL}${url}`;

      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired.');
        }

        const errorData = await response.json().catch(() => ({
          message: 'Unexpected error',
        }));

        throw new Error(errorData.message);
      }

      return isBlob ? response.blob() : response.json();
    },
    [navigate],
  );

  /* ------------------------------------------------------------------ */
  /*  CLEAR — every open starts from a completely empty form             */
  /* ------------------------------------------------------------------ */
  const clearForm = useCallback(() => {
    setForm(buildInitialForm());
    setErrors({});
    setAppOpen(false);
    setAppQuery('');
    setDdStyle(HIDDEN_DD_STYLE);
  }, []);

  /* ------------------------------------------------------------------ */
  /*  1) ACCOUNT TYPES (GAP-08a) — dropdown options + id mapping.        */
  /*  The enum is static server-side, so failures fall back to a local   */
  /*  mirror instead of blocking the invite.                             */
  /* ------------------------------------------------------------------ */
  const getAccountTypes = useCallback(
    async (shouldAbort = () => false) => {
      try {
        const response = await GetAPIcalls(
          `/api/v1/agency/customers/account-types`,
        );
        if (shouldAbort()) return;

        const raw = firstDefined(
          response?.data?.account_types,
          response?.account_types,
        );
        const status = Number(response?.status);

        if (status === 200 || Array.isArray(raw)) {
          const list = normalizeAccountTypes(raw);
          const next = list.length ? list : FALLBACK_ACCOUNT_TYPES;
          setAccountTypes(next);

          // default the dropdown to the first enum entry
          setForm((f) => {
            const keep =
              f.appType &&
              next.some((t) => t.label === f.appType || t.name === f.appType);
            return keep ? f : { ...f, appType: next[0]?.label ?? f.appType };
          });
        } else {
          if (status === 401) {
            localStorage.removeItem('jwtToken');
            navigate('/login');
          }
          if (status >= 400) {
            notify(
              response?.message || 'Could not load application types.',
              'error',
            );
          }
        }
      } catch (err) {
        if (shouldAbort()) return;
        // eslint-disable-next-line no-console
        console.warn(
          '[InviteCustomerModal] account-types API failed — using static enum fallback:',
          err,
        );
        setAccountTypes(FALLBACK_ACCOUNT_TYPES);
        setForm((f) =>
          f.appType ? f : { ...f, appType: FALLBACK_ACCOUNT_TYPES[0].label },
        );
        notify(err?.message || 'Could not load application types.', 'error');
      }
    },
    [GetAPIcalls, navigate, notify],
  );

  /* ------------------------------------------------------------------ */
  /*  2) VERIFICATION CATALOG — list + locks + default pre-selects.      */
  /*  shouldAbort() lets the open-effect drop a stale response when the  */
  /*  modal is closed/reopened before this resolves.                     */
  /* ------------------------------------------------------------------ */
  const getPackageDefaults = useCallback(
    async (shouldAbort = () => false) => {
      try {
        setPackageDefaultsLoading(true);
        const response = await GetAPIcalls(
          `/api/v1/agency/verification-package-defaults/catalog`,
        );
        if (shouldAbort()) return;

        const rawItems = firstDefined(
          response?.data?.packages,
          response?.data?.catalog,
          response?.data?.items,
          response?.data?.verification_packages,
          response?.packages,
        );
        const status = Number(response?.status);

        if (status === 200 || Array.isArray(rawItems)) {
          const data = response?.data ?? response ?? {};
          setPackageDefaults(data);

          // the catalog IS the checklist now (codes, names, lock flags)
          const items = withIdentity(pickCatalogItems(data));
          if (items.length) setCatalogItems(items);
          const listForSelection = items.length ? items : VERIFICATIONS;

          /* enable the defaults — a top-level codes array already
             resolved server-side wins; per-item is_default otherwise.
             The catalog API returns resolved_default_packages (identity +
             extras) and default_extras (extras only) — the other names are
             kept for the older defaults-endpoint shape. Identity is always
             merged back in by resolveSelectedFromCatalog either way. */
          const hints = firstDefined(
            data.resolved_default_packages, // ← what the catalog returns
            data.default_item_codes,
            data.item_codes,
            data.resolved_packages,
            data.default_codes,
            data.default_extras, // ← what the catalog returns
          );
          const selection = resolveSelectedFromCatalog(listForSelection, hints);
          setDefaultSelection(selection);
          setForm((f) => ({ ...f, verifications: selection }));
        } else {
          if (status === 401) {
            localStorage.removeItem('jwtToken');
            navigate('/login');
          }
          if (status >= 400) {
            notify(
              response?.message || 'Could not load verification defaults.',
              'error',
            );
          }
        }
      } catch (err) {
        if (shouldAbort()) return;
        notify(
          err?.message || 'Could not load verification defaults.',
          'error',
        );
        // never block the invite — identity alone is always valid
        setForm((f) => ({
          ...f,
          verifications: [...FALLBACK_VERIFICATIONS],
        }));
      } finally {
        if (!shouldAbort()) setPackageDefaultsLoading(false);
      }
    },
    [GetAPIcalls, navigate, notify],
  );

  useEffect(() => {
    if (!open) return undefined;

    // 1) wipe the form first — nothing from a previous invite survives
    clearForm();

    // 2) then pull the enum + catalog and pre-select the defaults
    let cancelled = false;
    getAccountTypes(() => cancelled);
    getPackageDefaults(() => cancelled);

    return () => {
      cancelled = true;
    };
  }, [open, clearForm, getAccountTypes, getPackageDefaults]);

  /* ------------------------------------------------------------------ */
  /*  3) INVITE POST — same shape as your changeSubAPI                   */
  /* ------------------------------------------------------------------ */
  const sendInviteAPI = useCallback(
    async (payload) => {
      const token = localStorage.getItem('jwtToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const apiUrl = `${BASE_URL}/api/v1/agency/customers/invite`;
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('jwtToken');
            navigate('/login');
            throw new Error('Session expired. Please log in again.');
          }
          const errorData = await response.json().catch(() => ({
            message: 'An unexpected error occurred',
          }));
          throw new Error(errorData.message || 'Something went wrong');
        }
        return await response.json();
      } catch (error) {
        throw new Error(
          error.message || 'Network error, please try again later',
        );
      }
    },
    [navigate],
  );

  /* Build the documented payload — every key is required by the API. */
  const buildInvitePayload = (f) => {
    const payload = {
      first_name: f.firstName.trim(),
      last_name: f.lastName.trim(),
      email_address: f.email.trim(),
      mobile_number: f.mobile.replace(/\D/g, ''),
      country_code: f.cc,
      // label shown in the dropdown → enum value from the account-types API
      account_type_id: getAccountTypeId(f.appType, accountTypes),
      // catalog codes (implied identity removed) — these are the item codes
      item_codes: toItemCodes(f.verifications),
    };

    /* certificate_validity_days: 30 | 60 | 90 | null ("No expiry" → null) */
    if (INCLUDE_CERTIFICATE_VALIDITY) {
      payload.certificate_validity_days =
        f.certValidity === 'none' ? null : Number(f.certValidity);
    }

    return payload;
  };

  /* The options list is a FIXED overlay (not in-flow) so it can never be
     clipped by the verifications panel, push the form taller, or add
     scroll to the body. It positions itself under the trigger and flips
     above it when there's no room below. */
  useLayoutEffect(() => {
    if (!appOpen || !triggerRef.current || !ddRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const h = ddRef.current.offsetHeight;
    const flip = r.bottom + 8 + h > window.innerHeight - 8 && r.top > h + 8;
    setDdStyle({
      position: 'fixed',
      left: r.left,
      width: r.width,
      top: flip ? r.top - h - 8 : r.bottom + 8,
      zIndex: 60,
    });
  }, [appOpen]);

  /* Close on outside click / Escape / resize / scroll — but never when
     interacting with the dropdown itself (typing in its search box,
     scrolling its option list, clicking an option). */
  useEffect(() => {
    if (!appOpen) return;
    const close = () => setAppOpen(false);
    const onDown = (e) => {
      if (ddRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    const onScroll = (e) => {
      if (ddRef.current?.contains(e.target)) return; // scrolling options
      close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    /* capture-phase so scrolls inside the form body close it too */
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [appOpen]);

  /* Dropdown shows the enum from the API (static mirror if it failed). */
  const appTypeOptions = (
    accountTypes.length ? accountTypes : FALLBACK_ACCOUNT_TYPES
  ).map((t) => t.label);
  const appOptions = appTypeOptions.filter((t) =>
    t.toLowerCase().includes(appQuery.trim().toLowerCase()),
  );

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = 'Enter a valid email';
    if (!form.mobile.replace(/\D/g, '').trim()) e.mobile = 'Required';
    if (form.verifications.length === 0)
      e.verifications = 'Select at least one verification';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* After a successful invite: empty form, but keep the defaults we
     already fetched so the next invite opens pre-ticked. */
  const resetForm = () => {
    setForm({
      ...buildInitialForm(),
      appType: accountTypes[0]?.label ?? FALLBACK_ACCOUNT_TYPES[0].label,
      verifications: [...defaultSelection],
    });
    setErrors({});
    setAppOpen(false);
    setAppQuery('');
  };

  const submit = async () => {
    if (!validate()) return;

    const payload = buildInvitePayload(form);
    setSubmitting(true);

    let toastMessage = null;
    try {
      const response = await sendInviteAPI(payload);

      // the body may carry the code as a number or a string; a body with
      // no status at all but success: true is still a success
      const status = Number(response?.status);
      const isSuccess =
        status === 200 || status === 201 || response?.success === true;

      if (isSuccess) {
        toastMessage = {
          message: response?.message || 'Invite sent successfully.',
          type: 'success',
        };

        /* Let the parent refresh its list — but in its OWN try/catch.
           An exception in the parent's handler (e.g. reading a field the
           API didn't return, like someField.toLowerCase()) used to fall
           into the catch below and get toasted as if the invite failed. */
        try {
          onSave?.(response?.data ?? payload);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(
            '[InviteCustomerModal] onSave handler threw — the invite itself succeeded:',
            err,
            response?.data ?? payload,
          );
        }

        resetForm();
        setSubmitting(false);
        onClose?.();
      } else {
        toastMessage = {
          message: response?.message || 'Something went wrong',
          type: 'error',
        };
      }
    } catch (err) {
      toastMessage = {
        message: err?.message || 'Network error, please try again later',
        type: 'error',
      };
    } finally {
      setSubmitting(false);
      if (toastMessage) {
        const { message, type } = toastMessage;
        setTimeout(() => notify(message, type), 300);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-ink/45 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* WIDE modal — two panes on md+ */}
      <div className="relative w-full sm:max-w-md md:max-w-3xl bg-white rounded-t-3xl sm:rounded-2xl shadow-lift animate-modal-in overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand/15 to-brand/5 ring-1 ring-brand/20 flex items-center justify-center text-brand">
              <UserPlus size={18} strokeWidth={1.9} />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">
                Invite customer
              </h2>
              <p className="text-[11px] text-slate-400">
                Send an invite so they can complete onboarding
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body — left: fields · right: verifications */}
        <div className="px-5 sm:px-6 py-5 max-h-[72vh] overflow-y-auto">
          <div className="md:grid md:grid-cols-[1.05fr_0.95fr] md:gap-6">
            {/* ---------------- Left pane: form fields ---------------- */}
            <div className="space-y-5">
              {/* Personal details */}
              <section>
                <SectionLabel icon={User} title="Personal details" />
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="First name"
                    icon={User}
                    required
                    error={errors.firstName}
                  >
                    <input
                      value={form.firstName}
                      onChange={(e) => set('firstName', e.target.value)}
                      placeholder="Jane"
                      className={inputCls(errors.firstName)}
                    />
                  </Field>
                  <Field
                    label="Last name"
                    icon={User}
                    required
                    error={errors.lastName}
                  >
                    <input
                      value={form.lastName}
                      onChange={(e) => set('lastName', e.target.value)}
                      placeholder="Doe"
                      className={inputCls(errors.lastName)}
                    />
                  </Field>
                </div>
              </section>

              {/* Contact */}
              <section>
                <SectionLabel icon={Mail} title="Contact" />
                <div className="space-y-3">
                  <Field
                    label="Email"
                    icon={Mail}
                    required
                    error={errors.email}
                  >
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => set('email', e.target.value)}
                      placeholder="jane@company.com.au"
                      className={inputCls(errors.email)}
                    />
                  </Field>
                  <Field
                    label="Mobile"
                    icon={Phone}
                    required
                    error={errors.mobile}
                  >
                    <div className="flex gap-2">
                      {/* Country code — shared CustomSelect (was a native <select>) */}
                      <CustomSelect
                        value={form.cc}
                        options={countries.map((c) => ({
                          value: c.dialCode,
                          label: `${c.code} ${c.dialCode}`,
                        }))}
                        onChange={(value) => set('cc', value)}
                        ariaLabel="Country code"
                        buttonClassName="h-11 w-28 rounded-xl px-2.5 text-sm font-semibold text-slate-700"
                      />
                      <input
                        value={form.mobile}
                        onChange={(e) =>
                          set('mobile', e.target.value.replace(/[^\d\s]/g, ''))
                        }
                        placeholder="412 345 678"
                        className={inputCls(errors.mobile, 'flex-1')}
                      />
                    </div>
                  </Field>
                </div>
              </section>

              {/* Role */}
              <section>
                <SectionLabel icon={BadgeCheck} title="Application type" />
                <div className="relative" data-searchselect>
                  <button
                    type="button"
                    ref={triggerRef}
                    onClick={() => setAppOpen((o) => !o)}
                    className="w-full h-11 rounded-xl border border-slate-200 bg-white pl-3.5 pr-10 text-sm text-left text-slate-800 shadow-sm transition-all duration-200 hover:border-slate-300 focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/10 flex items-center justify-between"
                  >
                    <span className="truncate">{form.appType}</span>
                    <ChevronDown
                      size={16}
                      className={`text-slate-400 transition-transform duration-200 ${appOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {appOpen && (
                    <div
                      ref={ddRef}
                      style={ddStyle}
                      className="rounded-xl border border-slate-200 bg-white shadow-lift overflow-hidden"
                    >
                      <div className="p-2 border-b border-slate-100">
                        <div className="relative">
                          <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                          />
                          <input
                            autoFocus
                            value={appQuery}
                            onChange={(e) => setAppQuery(e.target.value)}
                            placeholder="Search application type…"
                            className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 bg-slate-50/60 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/10 focus:bg-white transition-all"
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto py-1">
                        {appOptions.map((t) => {
                          const active = form.appType === t;
                          return (
                            <button
                              type="button"
                              key={t}
                              onClick={() => {
                                set('appType', t);
                                setAppOpen(false);
                                setAppQuery('');
                              }}
                              className={`w-full flex items-center gap-2 px-3.5 py-2.5 text-left text-[12.5px] font-medium transition-colors ${
                                active
                                  ? 'bg-brand/5 text-brand'
                                  : 'text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              <span
                                className={`w-4 h-4 rounded-md border flex items-center justify-center transition-colors ${
                                  active
                                    ? 'border-brand bg-brand text-white'
                                    : 'border-slate-300'
                                }`}
                              >
                                {active && <Check size={11} strokeWidth={3} />}
                              </span>
                              {t}
                            </button>
                          );
                        })}
                        {appOptions.length === 0 && (
                          <p className="px-3.5 py-3 text-[12px] text-slate-400">
                            No application type matches “{appQuery}”
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* Certificate validity */}
              <section>
                <SectionLabel
                  icon={CalendarClock}
                  title="Certificate validity"
                />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 rounded-xl bg-slate-100/80 p-1.5">
                  {CERT_VALIDITY_OPTIONS.map((o) => {
                    const active = form.certValidity === o.value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        onClick={() => set('certValidity', o.value)}
                        aria-pressed={active}
                        className={`h-9 rounded-lg px-1 text-[11.5px] font-semibold transition-all duration-200 ${
                          active
                            ? 'bg-white text-brand shadow-sm ring-1 ring-brand/20'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </section>
            </div>

            {/* ---------------- Right pane: verifications ---------------- */}
            <div className="mt-5 md:mt-0">
              <VerificationChecklist
                value={form.verifications}
                onChange={(next) => set('verifications', next)}
                loading={packageDefaultsLoading}
                items={catalogItems}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2.5 px-5 sm:px-6 py-3.5 border-t border-slate-100 bg-slate-50/50">
          <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
            <ShieldCheck size={13} className="text-brand/70" />
            {form.verifications.length} verification
            {form.verifications.length === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2.5 ml-auto">
            <button
              onClick={onClose}
              className="h-11 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || packageDefaultsLoading}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-gradient-to-r from-brand to-brand-dark text-white text-sm font-bold shadow-lg shadow-brand/25 hover:shadow-brand/40 hover:-translate-y-0.5 transition-all active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-brand/25"
            >
              {submitting ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Plus size={15} />
                  Send invite
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  DEMO-ONLY helpers — already exist in the real project file.        */
/*  Keep the project versions; these exist so this file is standalone. */
/*  DO NOT PASTE THIS BLOCK INTO YOUR PROJECT.                         */
/* ================================================================== */
const BASE_URL = process.env.REACT_APP_BASE_URL;

const COUNTRY_CODES = [
  { code: '+61', label: 'AU', flag: '🇦🇺' },
  { code: '+64', label: 'NZ', flag: '🇳🇿' },
  { code: '+44', label: 'UK', flag: '🇬🇧' },
  { code: '+1', label: 'US', flag: '🇺🇸' },
  { code: '+91', label: 'IN', flag: '🇮🇳' },
  { code: '+65', label: 'SG', flag: '🇸🇬' },
];

const inputCls = (error, extra = '') =>
  `w-full h-11 rounded-xl border bg-white pl-3.5 pr-3.5 text-sm text-slate-800 placeholder:text-slate-400 shadow-sm transition-all duration-200 focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/10 ${
    error
      ? 'border-red-300 bg-red-50/40 focus:ring-red-100'
      : 'border-slate-200 hover:border-slate-300 focus:bg-white'
  } ${extra}`;

function SectionLabel({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="w-5 h-5 rounded-md bg-brand/10 flex items-center justify-center text-brand">
        <Icon size={11} strokeWidth={2.2} />
      </span>
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
        {title}
      </span>
      <span className="h-px flex-1 bg-slate-100" />
    </div>
  );
}

function Field({ label, icon: Icon, required, error, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 mb-1.5">
        {label}
        {required && <span className="text-brand">*</span>}
        {error && (
          <span className="text-red-500 font-medium ml-1">{error}</span>
        )}
      </span>
      {children}
    </label>
  );
}

export {
  VERIFICATIONS,
  FALLBACK_VERIFICATIONS,
  FALLBACK_ACCOUNT_TYPES,
  IMPLIED_VERIFICATIONS,
  resolveSelectedVerifications,
  resolveSelectedFromCatalog,
  normalizeCatalogItem,
  normalizeAccountTypes,
  toItemCodes,
  getAccountTypeId,
  VerificationChecklist,
};
export default InviteCustomerModal;
